import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventIcon from "@mui/icons-material/Event";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PendingIcon from "@mui/icons-material/Pending";
import WorkIcon from "@mui/icons-material/Work";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import leaveApiService from "../../../services/leave-api";
import {
  EmployeeDashboard as DashboardData,
  LeaveBalance,
  LeaveRequest,
  LeaveRequestStatus,
  formatDate,
} from "../../../types/leave";
import NewRequestDialog from "../../../components/NewRequestDialog";
import EditRequestDialog from "../../../components/EditRequestDialog";

const EmployeeDashboard: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Dialog states
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(
    null,
  );

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leaveApiService.getMyDashboard();
      setDashboardData(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToLoadDashboardData"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    loadRequests();
  }, []);

  const handleRefresh = () => {
    fetchDashboardData();
    loadRequests();
  };

  const loadRequests = async () => {
    try {
      setRequestsLoading(true);
      const requestsData = await leaveApiService.getMyRequests();

      setRequests(Array.isArray(requestsData) ? requestsData : []);
    } catch {
      setRequests([]); // Ensure it's always an array on error
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleNewRequest = () => {
    setNewRequestOpen(true);
  };

  const handleCancelRequest = async (request: LeaveRequest) => {
    try {
      await leaveApiService.cancelLeaveRequest(request.id);
      handleRequestSuccess();
    } catch (err: any) {
      setError(err.message || t("error.failedToCancelRequest"));
    }
  };

  const handleRequestSuccess = () => {
    fetchDashboardData();
    loadRequests();
  };

  const handleEditRequest = (request: LeaveRequest) => {
    setSelectedRequest(request);
    setEditDialogOpen(true);
  };

  const canEditRequest = (request: LeaveRequest): boolean => {
    return request.status === LeaveRequestStatus.PENDING;
  };

  const getBalanceColor = (balance: LeaveBalance): string => {
    const percentage = (balance.availableDays / balance.allocatedDays) * 100;
    if (percentage <= 20) return theme.palette.error.main;
    if (percentage <= 50) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getStatusChipColor = (status: LeaveRequestStatus) => {
    switch (status) {
      case LeaveRequestStatus.PENDING:
        return "warning";
      case LeaveRequestStatus.APPROVED:
        return "success";
      case LeaveRequestStatus.DENIED:
        return "error";
      case LeaveRequestStatus.CANCELLED:
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton color="inherit" size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return <Alert severity="info">{t("common.loading")}</Alert>;
  }

  const { employee, currentYearBalances, pendingRequestsCount, upcomingLeave } =
    dashboardData;

  // Additional check for employee data
  if (!employee) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton color="inherit" size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {t("leave.employeeDataMissing")}
      </Alert>
    );
  }

  // Provide default values for arrays to prevent undefined errors
  const safeCurrentYearBalances = currentYearBalances || [];
  const safeUpcomingLeave = upcomingLeave || [];
  const safePendingRequestsCount = pendingRequestsCount || 0;

  return (
    <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
      <Grid container spacing={3}>
        {/* Employee Info Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <WorkIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6">{t("leave.userProfile")}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("leave.employeeId")}: {employee.employeeId}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {employee.firstName} {employee.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {employee.email}
              </Typography>
              {employee.department && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t("leave.department")}: {employee.department}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("leave.hireDate")}: {formatDate(employee.hireDate)}
              </Typography>
              {employee.yearsOfService && (
                <Typography variant="body2" color="text.secondary">
                  {t("leave.yearsOfService")}: {employee.yearsOfService}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Leave Balances Card */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TrendingUpIcon
                    sx={{ mr: 1, color: theme.palette.primary.main }}
                  />
                  <Typography variant="h6">
                    {t("leave.balances")} ({new Date().getFullYear()})
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewRequest}
                  size="small"
                >
                  {t("leave.newRequest")}
                </Button>
              </Box>
              {/* Filter to only show balances with allocated days > 0 */}
              {(() => {
                const activeBalances = safeCurrentYearBalances.filter(
                  (balance) => balance.allocatedDays > 0,
                );

                if (activeBalances.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {t("leave.noBalances")}
                    </Typography>
                  );
                }

                return (
                  <Box
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    {activeBalances.map((balance, index) => (
                      <Box
                        key={balance.id}
                        sx={{
                          p: 2,
                          borderBottom:
                            index < activeBalances.length - 1 ? 1 : 0,
                          borderColor: "divider",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        {/* Header Row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="medium">
                            {t(`leave.leaveTypes.${balance.leaveTypeCode}`)}
                          </Typography>
                          <Typography
                            variant="h6"
                            color={getBalanceColor(balance)}
                            fontWeight="bold"
                          >
                            {balance.availableDays} {t("leave.days")}
                          </Typography>
                        </Box>

                        {/* Progress Bar */}
                        <LinearProgress
                          variant="determinate"
                          value={
                            (balance.availableDays / balance.allocatedDays) *
                            100
                          }
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: theme.palette.grey[200],
                            mb: 1,
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: getBalanceColor(balance),
                              borderRadius: 3,
                            },
                          }}
                        />

                        {/* Details Row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("leave.usedDays")}:{" "}
                              <strong>{balance.usedDays}</strong>
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("leave.allocatedDays")}:{" "}
                              <strong>{balance.allocatedDays}</strong>
                            </Typography>
                            {balance.carriedOverDays > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t("leave.carriedOverDays")}:{" "}
                                <strong>{balance.carriedOverDays}</strong>
                              </Typography>
                            )}
                          </Box>
                          {!isMobile && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {Math.round(
                                (balance.availableDays /
                                  balance.allocatedDays) *
                                  100,
                              )}
                              % {t("leave.remaining")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Requests Summary */}
        {safePendingRequestsCount > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PendingIcon
                    sx={{ mr: 1, color: theme.palette.warning.main }}
                  />
                  <Typography variant="h6">
                    {t("leave.pendingRequests")}
                  </Typography>
                </Box>
                <Typography variant="h4" color="warning.main" gutterBottom>
                  {safePendingRequestsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {safePendingRequestsCount === 1
                    ? "request awaiting approval"
                    : "requests awaiting approval"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Upcoming Leave */}
        {safeUpcomingLeave.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <EventIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                  <Typography variant="h6">
                    {t("leave.upcomingLeave")}
                  </Typography>
                </Box>
                <List dense>
                  {safeUpcomingLeave.slice(0, 3).map((request) => (
                    <ListItem key={request.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <EventIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${t(`leave.leaveTypes.${request.leaveTypeCode}`)} - ${
                          request.isHalfDay
                            ? `${request.totalDays || 0.5} ${t("leave.days")} (${request.halfDayPeriod === "MORNING" ? "AM" : "PM"})`
                            : `${request.totalDays || 0} ${t("leave.days")}`
                        }`}
                        secondary={`${formatDate(request.startDate)} - ${formatDate(request.endDate)}`}
                      />
                      <Chip
                        label={t(`leave.status.${request.status}`)}
                        color={getStatusChipColor(request.status)}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Leave Requests */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">{t("leave.myRequests")}</Typography>
              </Box>

              {requestsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !Array.isArray(requests) || requests.length === 0 ? (
                <Alert severity="info">{t("leave.noRequests")}</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("leave.leaveType")}</TableCell>
                        <TableCell>{t("leave.dates")}</TableCell>
                        <TableCell align="center">{t("leave.days")}</TableCell>
                        <TableCell align="center">
                          {t("common.status")}
                        </TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(Array.isArray(requests) ? requests : []).map(
                        (request) => (
                          <TableRow key={request.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {t(`leave.leaveTypes.${request.leaveTypeCode}`)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(request.startDate)} -{" "}
                                {formatDate(request.endDate)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="medium">
                                {request.totalDays}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={t(`leave.status.${request.status}`)}
                                color={getStatusChipColor(request.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {request.status === LeaveRequestStatus.PENDING ? (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditRequest(request)}
                                    title={t("common.edit")}
                                    color="secondary"
                                    sx={{ mr: 1 }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCancelRequest(request)}
                                    title={t("leave.cancelRequest")}
                                    color="error"
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </>
                              ) : (
                                <IconButton
                                  size="small"
                                  disabled
                                  title={t("leave.requestCompleted")}
                                ></IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <NewRequestDialog
        open={newRequestOpen}
        onClose={() => setNewRequestOpen(false)}
        onSuccess={handleRequestSuccess}
      />

      <EditRequestDialog
        open={editDialogOpen}
        request={selectedRequest}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          handleRequestSuccess();
        }}
      />
    </Box>
  );
};

export default EmployeeDashboard;
